/**
 * AgendaScreen — agenda global del coach (web), timeline tipo Teams.
 *
 * Muestra TODAS las sesiones de los clientes agrupadas por día ("Hoy",
 * "Mañana", "lun 26 may"), con la hora a la izquierda — "sesión con Angy 4pm,
 * con Pepito 8pm". El coach fija la hora: agenda sesiones sueltas o recurrentes
 * (Lun/Mié/Vie 4pm × N semanas) y puede reagendar / cancelar / borrar cada una.
 *
 * Paridad con las apps móviles (coach/cliente ya tenían esta agenda). Misma API
 * (`api/appointments`) y misma regla de horas de Colombia (UTC-5).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "../lib/Icon";
import { useToast } from "../lib/toast";
import { listClients, type ClientFull } from "../api/clients";
import {
  listAllAppointments,
  createAppointment,
  createRecurringAppointments,
  updateAppointment,
  deleteAppointment,
  toColombiaIso,
  dayLabel,
  dayKey,
  timeLabel,
  type CoachAppointment,
} from "../api/appointments";
import type { Route } from "../types";

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"]; // 0=Lun … 6=Dom (backend)
const DURATIONS = [30, 45, 60, 90];
const MODALITIES: Array<{ key: string; label: string; icon: string }> = [
  { key: "presencial", label: "Presencial", icon: "user" },
  { key: "virtual", label: "Virtual", icon: "chat" },
  { key: "hibrido", label: "Híbrido", icon: "swap" },
];

function modalityLabel(m: string | null): string {
  if (!m) return "";
  return MODALITIES.find((x) => x.key === m)?.label || m;
}

function todayYmd(): string {
  const d = new Date();
  return dayKey(d);
}

interface DayGroup {
  key: string;
  date: Date;
  items: CoachAppointment[];
}

type Tab = "upcoming" | "history";

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  scheduled: { label: "Programada", cls: "is-scheduled" },
  completed: { label: "Realizada", cls: "is-done" },
  cancelled: { label: "Cancelada", cls: "is-cancelled" },
  no_show: { label: "No vino", cls: "is-noshow" },
};

export function AgendaScreen({ go }: { go: (r: Route, query?: string) => void }) {
  void go;
  const toast = useToast();
  const [appts, setAppts] = useState<CoachAppointment[] | null>(null);
  const [clients, setClients] = useState<ClientFull[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("upcoming");
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editing, setEditing] = useState<CoachAppointment | null>(null);

  const reload = useCallback(async () => {
    try {
      const list = await listAllAppointments();
      setAppts(list);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    reload();
    listClients()
      .then((cs) => setClients(cs.filter((c) => c.is_active !== false)))
      .catch(() => {});
  }, [reload]);

  // Agrupa por día. La pestaña "Próximas" muestra de hoy en adelante (futuras
  // scheduled). "Historial" muestra pasadas (completed/cancelled/no_show, y
  // también scheduled pasadas por si el cron lazy no corrió todavía), más
  // recientes primero.
  const startOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, []);

  const upcomingCount = useMemo(() => {
    if (!appts) return 0;
    return appts.filter(
      (a) => a.status === "scheduled" && new Date(a.starts_at).getTime() >= startOfToday,
    ).length;
  }, [appts, startOfToday]);

  const historyCount = useMemo(() => {
    if (!appts) return 0;
    return appts.filter((a) => {
      const t = new Date(a.starts_at).getTime();
      const past = t < startOfToday;
      // canceladas, completadas y no-show siempre cuentan al historial; las
      // scheduled solo si su fecha ya pasó (cron lazy todavía no las tocó).
      return a.status !== "scheduled" || past;
    }).length;
  }, [appts, startOfToday]);

  const groups = useMemo<DayGroup[]>(() => {
    if (!appts) return [];
    const visible = appts.filter((a) => {
      const t = new Date(a.starts_at).getTime();
      if (tab === "upcoming") {
        // próximas: scheduled de hoy en adelante (escondemos canceladas
        // futuras — viven en historial para no estorbar la planificación).
        return a.status === "scheduled" && t >= startOfToday;
      }
      // historial: cualquier estado terminal + scheduled pasadas
      return a.status !== "scheduled" || t < startOfToday;
    });
    const map = new Map<string, DayGroup>();
    for (const a of visible) {
      const d = new Date(a.starts_at);
      const k = dayKey(d);
      if (!map.has(k))
        map.set(k, { key: k, date: new Date(d.getFullYear(), d.getMonth(), d.getDate()), items: [] });
      map.get(k)!.items.push(a);
    }
    const arr = Array.from(map.values());
    // próximas ascendente (lo más cercano arriba); historial descendente
    // (lo más reciente arriba)
    arr.sort((x, y) =>
      tab === "upcoming" ? x.date.getTime() - y.date.getTime() : y.date.getTime() - x.date.getTime(),
    );
    arr.forEach((g) =>
      g.items.sort((x, y) =>
        tab === "upcoming" ? x.starts_at.localeCompare(y.starts_at) : y.starts_at.localeCompare(x.starts_at),
      ),
    );
    return arr;
  }, [appts, tab, startOfToday]);

  const todayKey = todayYmd();

  return (
    <div className="al-ag">
      <div className="al-ag-head">
        <div>
          <h1 className="al-ag-title">Agenda</h1>
          <p className="al-ag-sub">Tus sesiones con clientes — el horario lo fijás vos.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setScheduleOpen(true)} disabled={clients.length === 0}>
          <Icon name="plus" size={14} /> Agendar sesión
        </button>
      </div>

      <div className="al-ag-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === "upcoming"}
          className={"al-ag-tab" + (tab === "upcoming" ? " is-on" : "")}
          onClick={() => setTab("upcoming")}
        >
          Próximas <span className="al-ag-tab-count">{upcomingCount}</span>
        </button>
        <button
          role="tab"
          aria-selected={tab === "history"}
          className={"al-ag-tab" + (tab === "history" ? " is-on" : "")}
          onClick={() => setTab("history")}
        >
          Historial <span className="al-ag-tab-count">{historyCount}</span>
        </button>
      </div>

      {err ? (
        <div className="al-ag-empty">No pude cargar la agenda: {err}</div>
      ) : !appts ? (
        <div className="al-ag-empty">Cargando agenda…</div>
      ) : groups.length === 0 ? (
        <div className="al-ag-empty">
          <div className="al-ag-empty-mark"><Icon name="calendar" size={28} /></div>
          <b>{tab === "upcoming" ? "Sin sesiones agendadas" : "Aún no hay historial"}</b>
          <p className="tiny muted" style={{ maxWidth: 360 }}>
            {tab === "history"
              ? "Acá vas a ver tus sesiones realizadas, canceladas y no asistidas a medida que pasen."
              : clients.length === 0
                ? "Primero creá clientes para poder agendarles sesiones."
                : "Tocá “Agendar sesión” para crear una sesión suelta o recurrente (ej. Lun/Mié/Vie 4pm)."}
          </p>
        </div>
      ) : (
        <div className="al-ag-timeline">
          {groups.map((g) => (
            <div key={g.key} className="al-ag-day">
              <div className={"al-ag-day-head" + (g.key === todayKey ? " is-today" : "")}>
                <span className="al-ag-day-label">{dayLabel(g.date)}</span>
                <span className="tiny muted">
                  {g.items.length} {g.items.length === 1 ? "sesión" : "sesiones"}
                </span>
              </div>
              {g.items.map((a) => {
                const badge = STATUS_BADGE[a.status];
                const dim = a.status === "cancelled" || a.status === "no_show";
                return (
                  <button
                    key={a.id}
                    className={"al-ag-row " + (badge?.cls || "")}
                    onClick={() => setEditing(a)}
                    title="Editar / marcar / borrar"
                  >
                    <div className="al-ag-time">
                      <b>{timeLabel(a.starts_at)}</b>
                      <span className="tiny muted">{a.duration_min} min</span>
                    </div>
                    <div className="al-ag-rule" />
                    <div className="al-ag-avatar">{(a.client_name || "?").slice(0, 2).toUpperCase()}</div>
                    <div className="al-ag-info">
                      <div className="al-ag-client" style={dim ? { textDecoration: "line-through" } : undefined}>
                        {a.client_name}
                      </div>
                      <div className="tiny muted">
                        {a.title || "Sesión"}
                        {a.modality ? ` · ${modalityLabel(a.modality)}` : ""}
                      </div>
                    </div>
                    {badge && a.status !== "scheduled" ? (
                      <span className={"al-ag-badge " + badge.cls}>{badge.label}</span>
                    ) : null}
                    <Icon name="arrow" size={14} />
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {scheduleOpen && (
        <ScheduleModal
          clients={clients}
          onClose={() => setScheduleOpen(false)}
          onDone={async (msg) => {
            setScheduleOpen(false);
            toast(msg);
            await reload();
          }}
        />
      )}

      {editing && (
        <EditModal
          appt={editing}
          onClose={() => setEditing(null)}
          onDone={async (msg) => {
            setEditing(null);
            toast(msg);
            await reload();
          }}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// ScheduleModal — agendar sesión (suelta o recurrente) para un cliente
// ----------------------------------------------------------------------

function ScheduleModal({
  clients,
  onClose,
  onDone,
}: {
  clients: ClientFull[];
  onClose: () => void;
  onDone: (msg: string) => Promise<void>;
}) {
  const toast = useToast();
  const [clientId, setClientId] = useState<number | null>(clients[0]?.id ?? null);
  const [mode, setMode] = useState<"recurring" | "single">("recurring");
  const [busy, setBusy] = useState(false);

  // Campos compartidos
  const [time, setTime] = useState("16:00");
  const [duration, setDuration] = useState(60);
  const [modality, setModality] = useState<string>("presencial");
  const [title, setTitle] = useState("");

  // Recurrente
  const [weekdays, setWeekdays] = useState<number[]>([0, 2, 4]); // Lun/Mié/Vie
  const [startDate, setStartDate] = useState(todayYmd());
  const [weeks, setWeeks] = useState(4);

  // Suelta
  const [date, setDate] = useState(todayYmd());

  const toggleDay = (i: number) =>
    setWeekdays((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i].sort((a, b) => a - b)));

  const submit = async () => {
    if (!clientId || busy) return;
    setBusy(true);
    try {
      if (mode === "recurring") {
        if (weekdays.length === 0) {
          toast("Elegí al menos un día de la semana");
          setBusy(false);
          return;
        }
        const created = await createRecurringAppointments(clientId, {
          weekdays,
          time,
          start_date: startDate,
          weeks,
          duration_min: duration,
          modality,
          title: title.trim() || null,
        });
        await onDone(`${created.length} sesiones agendadas`);
      } else {
        await createAppointment(clientId, {
          starts_at: toColombiaIso(date, time),
          duration_min: duration,
          modality,
          title: title.trim() || null,
        });
        await onDone("Sesión agendada");
      }
    } catch (e) {
      toast("No pude agendar: " + (e instanceof Error ? e.message : String(e)));
      setBusy(false);
    }
  };

  return (
    <div className="al-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="al-modal" style={{ maxWidth: 520, width: "94vw" }} onClick={(e) => e.stopPropagation()}>
        <div className="al-modal-hd">
          <h3 style={{ margin: 0, fontSize: 16 }}>Agendar sesión</h3>
          <button className="icon-btn" onClick={onClose} disabled={busy} title="Cerrar"><Icon name="x" size={16} /></button>
        </div>

        <div className="al-modal-body" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14, flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}>
          {/* Cliente */}
          <label className="al-ag-field">
            <span className="al-ag-flabel">Cliente</span>
            <select className="al-input" value={clientId ?? ""} onChange={(e) => setClientId(Number(e.target.value))}>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.full_name}</option>
              ))}
            </select>
          </label>

          {/* Modo */}
          <div className="al-ag-segment">
            <button className={mode === "recurring" ? "is-on" : ""} onClick={() => setMode("recurring")} type="button">
              Recurrentes
            </button>
            <button className={mode === "single" ? "is-on" : ""} onClick={() => setMode("single")} type="button">
              Una sola
            </button>
          </div>

          {mode === "recurring" ? (
            <>
              <div className="al-ag-field">
                <span className="al-ag-flabel">Días de la semana</span>
                <div className="al-ag-days">
                  {WEEKDAYS.map((w, i) => (
                    <button
                      key={w}
                      type="button"
                      className={"al-ag-day-chip" + (weekdays.includes(i) ? " is-on" : "")}
                      onClick={() => toggleDay(i)}
                    >
                      {w}
                    </button>
                  ))}
                </div>
              </div>
              <div className="al-ag-row2">
                <label className="al-ag-field">
                  <span className="al-ag-flabel">Desde</span>
                  <input type="date" className="al-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </label>
                <label className="al-ag-field">
                  <span className="al-ag-flabel">Semanas</span>
                  <input type="number" min={1} max={53} className="al-input" value={weeks}
                    onChange={(e) => setWeeks(Math.max(1, Math.min(53, Number(e.target.value) || 1)))} />
                </label>
              </div>
            </>
          ) : (
            <label className="al-ag-field">
              <span className="al-ag-flabel">Fecha</span>
              <input type="date" className="al-input" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
          )}

          <div className="al-ag-row2">
            <label className="al-ag-field">
              <span className="al-ag-flabel">Hora</span>
              <input type="time" className="al-input" value={time} onChange={(e) => setTime(e.target.value)} />
            </label>
            <div className="al-ag-field">
              <span className="al-ag-flabel">Duración</span>
              <div className="al-ag-chips">
                {DURATIONS.map((d) => (
                  <button key={d} type="button" className={"al-ag-chip" + (duration === d ? " is-on" : "")} onClick={() => setDuration(d)}>
                    {d}′
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="al-ag-field">
            <span className="al-ag-flabel">Modalidad</span>
            <div className="al-ag-chips">
              {MODALITIES.map((m) => (
                <button key={m.key} type="button" className={"al-ag-chip" + (modality === m.key ? " is-on" : "")} onClick={() => setModality(m.key)}>
                  <Icon name={m.icon} size={12} /> {m.label}
                </button>
              ))}
            </div>
          </div>

          <label className="al-ag-field">
            <span className="al-ag-flabel">Título (opcional)</span>
            <input type="text" className="al-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej. Sesión de fuerza" />
          </label>
        </div>

        <div className="al-modal-ft" style={{ display: "flex", gap: 8, justifyContent: "flex-end", padding: 16, flexShrink: 0, borderTop: "1px solid var(--border)" }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy || !clientId}>
            {busy ? "Agendando…" : mode === "recurring" ? "Crear recurrentes" : "Agendar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// EditModal — reagendar / cancelar / borrar una sesión
// ----------------------------------------------------------------------

function EditModal({
  appt,
  onClose,
  onDone,
}: {
  appt: CoachAppointment;
  onClose: () => void;
  onDone: (msg: string) => Promise<void>;
}) {
  const toast = useToast();
  const start = new Date(appt.starts_at);
  const [date, setDate] = useState(dayKey(start));
  const [time, setTime] = useState(
    `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`,
  );
  const [duration, setDuration] = useState(appt.duration_min);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<void>, msg: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      await onDone(msg);
    } catch (e) {
      toast("Error: " + (e instanceof Error ? e.message : String(e)));
      setBusy(false);
    }
  };

  return (
    <div className="al-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="al-modal" style={{ maxWidth: 460, width: "92vw" }} onClick={(e) => e.stopPropagation()}>
        <div className="al-modal-hd">
          <h3 style={{ margin: 0, fontSize: 16 }}>{appt.client_name}</h3>
          <button className="icon-btn" onClick={onClose} disabled={busy} title="Cerrar"><Icon name="x" size={16} /></button>
        </div>

        <div className="al-modal-body" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14, flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}>
          {appt.status === "cancelled" && (
            <div className="al-ag-banner">Esta sesión está cancelada.</div>
          )}
          {appt.status === "completed" && (
            <div className="al-ag-banner is-done">Sesión realizada.</div>
          )}
          {appt.status === "no_show" && (
            <div className="al-ag-banner is-noshow">Marcada como "no vino".</div>
          )}
          <div className="al-ag-row2">
            <label className="al-ag-field">
              <span className="al-ag-flabel">Fecha</span>
              <input type="date" className="al-input" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            <label className="al-ag-field">
              <span className="al-ag-flabel">Hora</span>
              <input type="time" className="al-input" value={time} onChange={(e) => setTime(e.target.value)} />
            </label>
          </div>
          <div className="al-ag-field">
            <span className="al-ag-flabel">Duración</span>
            <div className="al-ag-chips">
              {DURATIONS.map((d) => (
                <button key={d} type="button" className={"al-ag-chip" + (duration === d ? " is-on" : "")} onClick={() => setDuration(d)}>
                  {d}′
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="al-modal-ft" style={{ display: "flex", gap: 8, alignItems: "center", padding: 16, flexShrink: 0, borderTop: "1px solid var(--border)", flexWrap: "wrap" }}>
          <button
            className="btn btn-ghost"
            style={{ color: "var(--danger, #dc2626)" }}
            disabled={busy}
            onClick={() => {
              if (confirm("¿Borrar esta sesión definitivamente?")) {
                run(() => deleteAppointment(appt.id), "Sesión borrada");
              }
            }}
          >
            Borrar
          </button>
          {appt.status === "scheduled" && (
            <>
              <button
                className="btn btn-ghost"
                disabled={busy}
                onClick={() => run(() => updateAppointment(appt.id, { status: "completed" }).then(() => {}), "Marcada como realizada")}
                title="Registrar que el cliente sí vino"
              >
                Marcar realizada
              </button>
              <button
                className="btn btn-ghost"
                disabled={busy}
                onClick={() => run(() => updateAppointment(appt.id, { status: "no_show" }).then(() => {}), "Marcada como no vino")}
                title="El cliente no se presentó"
              >
                No vino
              </button>
              <button
                className="btn btn-ghost"
                disabled={busy}
                onClick={() => run(() => updateAppointment(appt.id, { status: "cancelled" }).then(() => {}), "Sesión cancelada")}
              >
                Cancelar sesión
              </button>
            </>
          )}
          {appt.status !== "scheduled" && (
            <button
              className="btn btn-ghost"
              disabled={busy}
              onClick={() => run(() => updateAppointment(appt.id, { status: "scheduled" }).then(() => {}), "Estado revertido")}
              title="Volver a marcar como programada"
            >
              Reabrir
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button
            className="btn btn-primary"
            disabled={busy}
            onClick={() =>
              run(
                () => updateAppointment(appt.id, { starts_at: toColombiaIso(date, time), duration_min: duration, status: "scheduled" }).then(() => {}),
                "Sesión reagendada",
              )
            }
          >
            {busy ? "Guardando…" : "Reagendar"}
          </button>
        </div>
      </div>
    </div>
  );
}
